// SaveChannelFlow — one notification-channel write, end to end (contract 22 §2, ORP-18886).
//
// Headless by contract: it returns an outcome and never toasts or resolves i18n — the pane owns the
// copy (§13). A failed write propagates its `ApiError`; the pane re-reads afterwards either way, so
// the screen can never show a state the server did not confirm (that is legacy defect D6).
//
// It exists mostly to own ONE rule that legacy smeared across two components, two emits and a
// `setTimeout`: saving recipients also flips the channel. `NotificationUpdateModal.vue:190-196`
// computed a `{useValue, value}` pair, emitted it up to `NotificationCard.vue:103-105`, which
// re-entered `changeStatus`. Here it is a second request in the same method.
import { NotificationChannelsApi } from '../api/NotificationChannelsApi'
import type { NotificationChannel } from '../models/channel.types'
import { enableIntent } from '../service/channelValidation'

import { useWorkspace } from '@/features/account'

export interface SaveChannelOptions {
  /** What the channel held before this save — the input to the derived enable/disable. Omit it
   *  (or pass the same list) to send no enable request at all. */
  previousValues?: readonly string[]
  /**
   * ⚠ ORP-19876's third body shape. When set, the write targets a NAMED field of the channel
   * (`google_allowed_domains` / `google_allowed_emails`) rather than its recipient list, so the
   * derived enable/disable is skipped: a Google-Sheet allow-list is not a recipient list and
   * emptying it must not disable email notifications.
   */
  field?: string
  /** Write to a workspace other than the selected one. Defaults to the selected workspace. */
  workspaceId?: number | string
}

export interface SaveChannelResult {
  /** `true` the channel was enabled, `false` disabled, `null` no enable request was sent. */
  enableIntent: boolean | null
}

export interface SaveChannelFlowOptions {
  api?: NotificationChannelsApi
}

export class SaveChannelFlow {
  private readonly api: NotificationChannelsApi

  constructor(options: SaveChannelFlowOptions = {}) {
    this.api = options.api ?? new NotificationChannelsApi()
  }

  /** Persist a channel's values, then apply the derived enable/disable if the save implied one. */
  async save(
    channel: NotificationChannel,
    values: readonly string[],
    options: SaveChannelOptions = {},
  ): Promise<SaveChannelResult> {
    const workspaceId = this.workspace(options)
    // Empty slots never reach the wire: legacy seeds webhook channels with a `[null]` and filters
    // at the last moment (`NotificationUpdateModal.vue:200`).
    const next = values.filter(Boolean)

    await this.api.updateValues(workspaceId, { channel, values: next, ...this.fieldOf(options) })

    const intent =
      options.field === undefined ? enableIntent(options.previousValues ?? [], next) : null
    if (intent !== null) await this.api.setEnabled(workspaceId, { channel, enabled: intent })

    return { enableIntent: intent }
  }

  /** The toggle in the row (`PUT …/enable/`), with no recipient write attached. */
  async setEnabled(
    channel: NotificationChannel,
    enabled: boolean,
    options: SaveChannelOptions = {},
  ): Promise<void> {
    await this.api.setEnabled(this.workspace(options), { channel, enabled })
  }

  private fieldOf(options: SaveChannelOptions): { field?: string } {
    return options.field === undefined ? {} : { field: options.field }
  }

  private workspace(options: SaveChannelOptions): number | string {
    const target = options.workspaceId ?? useWorkspace().workspaceId.value
    if (target === undefined) {
      throw new Error('SaveChannelFlow: no workspace in scope for a notification write')
    }
    return target
  }
}
